import { EnhancedTabs, TabItem } from 'components/enhanced/tabs/EnhancedTabs';
import { useIsBottomFromTenantAdmin } from 'shared/permissions/useIsBottomFromTenantAdmin';
import { IsSuperAdminGuard } from 'shared/permissions/IsSuperAdmin';
import { ProductWarehouseHistoryLinkTab } from 'features/cost-analysis/common-tabs/layerTabs';
import { EntityHistoryLedgerCard } from 'features/settings/history/components/DataGridHistoryCard';
import { TenantPage } from 'layouts/TenantPage';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { examService, examTagService, macroExamService } from 'shared/services';
import { examHistoryLineService } from 'features/cost-analysis/exams/api/ExamHistoryService';
import { loadingActions } from 'store';
import { useAppDispatch } from 'store/store';
import { Option } from 'shared/types/general';
import toast from 'shared/utils/toast';
import ExamForm from '../compoents/ExamForm';
import ExamProductForm from '../compoents/HandleExamProduct';
import { IExam } from '../types';
import { ExamHistoryLineColumns } from '../exam-history-lines';

export const EditExam = () => {
  const translationPrefix = 'pages.exams.edit';
  const { t } = useTranslation();
  const { examId } = useParams();

  const [exam, setExam] = useState<IExam>();
  const [tagOptions, setTagOptions] = useState<string[]>([]);
  const [macroExamOptions, setMacroExamOptions] = useState<string[]>([]);
  const isTenantAdminBottom = useIsBottomFromTenantAdmin();
  const isSuperAdminGuard = IsSuperAdminGuard();

  useEffect(() => {
    examService.get(+examId).then(setExam);
    examTagService
      .getAllBaseInformation()
      .then((tags: Option[]) => {
        if (tags && Array.isArray(tags)) {
          setTagOptions(tags.map((tag) => tag.code));
        } else {
          setTagOptions([]);
        }
      })
      .catch(() => {
        setTagOptions([]);
      });
    macroExamService
      .getAllBaseInformation()
      .then((macroExams: Option[]) => {
        if (macroExams && Array.isArray(macroExams)) {
          setMacroExamOptions(macroExams.map((me) => me.code));
        } else {
          setMacroExamOptions([]);
        }
      })
      .catch(() => {
        setMacroExamOptions([]);
      });
  }, []);

  const dispatch = useAppDispatch();

  const update = async (values) => {
    dispatch(loadingActions.startLoading());

    // mettiamo un controllo frontend sulla possibilità di aggiungere 1 etichetta ad un esame
    if (values.tags && values.tags.length > 1) {
      toast.warning(t('pages.exams.edit.max_one_tag_error'));
      dispatch(loadingActions.stopLoading());
      return;
    }

    examService
      .update(exam.id, values)
      .then((updated) => {
        setExam(updated);
        toast.success(t(`${translationPrefix}.success`));
      })
      .catch((error) => {
        const errorMessage =
          error?.response?.data?.detail || error?.message || t('actions.error.updated');
        toast.error(errorMessage);
      })
      .finally(() => {
        dispatch(loadingActions.stopLoading());
      });
  };

  // -------------------------------------- HISTORY MANAGEMENT
  const historyRefresher = useRef(null);
  const handleDateTimeUpdate = (id: number, datetime: string) => {
    examHistoryLineService.update(id, { datetime: datetime }).then(() => {
      toast.success(t('actions.successfully.updated'));
      if (historyRefresher.current) {
        historyRefresher.current(); // ✅ Assicura che venga chiamata solo se assegnata
      }
    });
  };

  const handleDeleteLine = (id: number) => {
    examHistoryLineService.delete(id).then(() => {
      toast.success(t('actions.successfully.deleted'));
      if (historyRefresher.current) {
        historyRefresher.current(); // ✅ Assicura che venga chiamata solo se assegnata
      }
    });
  };

  const historyColumns = useMemo(
    () =>
      ExamHistoryLineColumns(
        handleDateTimeUpdate,
        handleDeleteLine,
        false,
        tagOptions,
        macroExamOptions
      ),
    [handleDateTimeUpdate, handleDeleteLine, tagOptions, macroExamOptions]
  );

  const tabConfig: TabItem[] = exam
    ? [
        {
          label: t('global.details'),
          component: <ExamForm exam={exam} onSubmit={update} disableEdit={!isTenantAdminBottom} />
        },
        {
          label: t(`global.products`),
          component: <ExamProductForm exam={exam} disableEdit={!isTenantAdminBottom} />
        },
        {
          label: t('global.history_lines'),
          visible: isSuperAdminGuard,
          variant: 'admin',
          component: (
            <EntityHistoryLedgerCard
              service={examHistoryLineService}
              columns={historyColumns}
              outerParams={{ exam: exam.code, exam__exact: 'true' }}
              refreshTriggerRef={historyRefresher}
            />
          )
        },
        {
          label: t(`global.history-update`),
          visible: isSuperAdminGuard,
          variant: 'admin',
          component: (
            <ProductWarehouseHistoryLinkTab
              title={t('global.exam')}
              layer={'exam'}
              entity_id={exam.id}
            />
          )
        }
      ]
    : [];

  return (
    <TenantPage
      title={t(`${translationPrefix}.title`)}
      subtitle={t(`${translationPrefix}.subtitle`)}
      entityCode={exam?.code}>
      <EnhancedTabs
        tabs={tabConfig}
        variant="scrollable"
        documentConfig={{
          serviceName: 'exam',
          entityId: exam?.id,
          dictParams: { exam: exam?.id },
          customFolderName: `menu.exam/${exam?.code}`,
          folderDescription: `Descrizione: ${exam?.description}; Etichetta: ${exam?.category}; `
        }}
        historyConfig={{ service: examService, entityId: exam?.id }}
      />
    </TenantPage>
  );
};

export default EditExam;
